import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Download, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Enrolled", "Closed"];
const SOURCES = ["Website", "Instagram", "Facebook", "WhatsApp"];
const srcBadge = { Website: "bg-blue-50 text-blue-800", Instagram: "bg-pink-50 text-pink-700", Facebook: "bg-indigo-50 text-indigo-700", WhatsApp: "bg-green-50 text-green-700" };
const badge = { New: "bg-sky-100 text-sky-800", Contacted: "bg-amber-100 text-amber-800", Enrolled: "bg-emerald-100 text-emerald-800", Closed: "bg-slate-100 text-slate-600" };

export default function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [srcFilter, setSrcFilter] = useState("All");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/enquiries"); setItems(data); }
    catch { toast.error("Failed to load enquiries"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/enquiries/${id}`, { status }); setItems((p) => p.map((e) => e.id === id ? { ...e, status } : e)); toast.success("Status updated"); }
    catch { toast.error("Update failed"); }
  };
  const updateSource = async (id, source) => {
    try { await api.patch(`/admin/enquiries/${id}`, { source }); setItems((p) => p.map((e) => e.id === id ? { ...e, source } : e)); toast.success("Source updated"); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/admin/enquiries/${id}`); setItems((p) => p.filter((e) => e.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = items.filter((e) => (filter === "All" || e.status === filter) && (srcFilter === "All" || (e.source || "Website") === srcFilter));

  const exportCsv = () => {
    const cols = ["created_at", "student_name", "parent_name", "mobile", "whatsapp", "email", "current_standard", "school_name", "course_interested", "preferred_batch", "message", "source", "status"];
    const header = cols.join(",");
    const rows = filtered.map((e) => cols.map((c) => `"${String(e[c] ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "ptg-enquiries.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="panel-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Admission Enquiries</h2>
          <p className="text-sm text-slate-500">{items.length} total • {items.filter((e) => e.status === "New").length} new</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36" data-testid="enquiry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All", ...STATUSES].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={srcFilter} onValueChange={setSrcFilter}>
            <SelectTrigger className="w-36" data-testid="enquiry-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} data-testid="enquiry-export-btn"><Download className="mr-1 h-4 w-4" /> CSV</Button>
        </div>
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-blue-900" /> : filtered.length === 0 ? (
        <div className="text-center text-slate-500 py-16 flex flex-col items-center gap-3"><Inbox className="h-10 w-10 text-slate-300" /> No enquiries found.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Parent</TableHead>
                <TableHead>Contact</TableHead><TableHead>Course</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => (
                <TableRow key={e.id} data-testid={`enquiry-row-${i}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDate(e.created_at)}</TableCell>
                  <TableCell>
                    <div className="font-semibold text-slate-800">{e.student_name || <span className="font-normal italic text-slate-400">Student name not given</span>}</div>
                    <div className="text-xs text-slate-500">{e.current_standard} {e.school_name ? `• ${e.school_name}` : ""}</div>
                  </TableCell>
                  <TableCell className="text-slate-700">{e.parent_name}</TableCell>
                  <TableCell className="text-xs">
                    <div>{e.mobile}</div>
                    {e.email && <div className="text-slate-500">{e.email}</div>}
                  </TableCell>
                  <TableCell className="text-slate-700">{e.course_interested}</TableCell>
                  <TableCell>
                    <Select value={e.source || "Website"} onValueChange={(v) => updateSource(e.id, v)}>
                      <SelectTrigger className={`h-8 w-32 text-xs font-semibold border-0 ${srcBadge[e.source || "Website"]}`} data-testid={`enquiry-source-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                      <SelectTrigger className={`h-8 w-32 text-xs font-semibold border-0 ${badge[e.status] || ""}`} data-testid={`enquiry-status-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => remove(e.id)} data-testid={`enquiry-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
